// MinIO Configuration
const MINIO_ENDPOINT = process.env.REACT_APP_MINIO_ENDPOINT || 'https://cdn.algios.dev';
const BUCKET_NAME = process.env.REACT_APP_MINIO_BUCKET || 'sicilia-artwork-tracker';

/**
 * Upload an image file directly to MinIO via HTTP PUT
 * Uses anonymous upload (bucket must have 'public' policy)
 * 
 * @param file - File to upload
 * @param artworkId - Artwork ID to organize files
 * @returns Promise with the public URL of the uploaded image
 */
export const uploadImage = async (file: File, artworkId: string): Promise<string> => {
  try {
    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `artworks/${artworkId}/${timestamp}.${fileExtension}`;

    // Build the upload URL
    const uploadUrl = `${MINIO_ENDPOINT}/${BUCKET_NAME}/${fileName}`;

    console.log('Uploading to MinIO:', uploadUrl);

    // Upload with anonymous PUT (bucket must have 'public' policy)
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    // Return public URL
    const publicUrl = `${MINIO_ENDPOINT}/${BUCKET_NAME}/${fileName}`;
    console.log('Upload successful:', publicUrl);
    return publicUrl;
  } catch (error) {
    console.error('Error uploading image to MinIO:', error);
    throw new Error('Failed to upload image to MinIO. Check console for details.');
  }
};

/**
 * Delete an image from MinIO
 * @param fileUrl - Full URL of the file to delete
 */
export const deleteImage = async (fileUrl: string): Promise<void> => {
  try {
    // Extract filename from URL
    const urlParts = fileUrl.split(`/${BUCKET_NAME}/`);
    if (urlParts.length < 2) {
      throw new Error('Invalid file URL');
    }
    const fileName = urlParts[1];

    // Build delete URL
    const deleteUrl = `${MINIO_ENDPOINT}/${BUCKET_NAME}/${fileName}`;

    // Try anonymous DELETE
    const response = await fetch(deleteUrl, {
      method: 'DELETE',
    });

    if (!response.ok && response.status !== 404) {
      console.warn(`Delete returned ${response.status}, but continuing anyway`);
    }
  } catch (error) {
    console.error('Error deleting image from MinIO:', error);
    // Don't throw - deletion failures are not critical
  }
};

/**
 * Delete all images for a specific artwork
 * Note: This requires listing objects first, which may not work without proper S3 API
 * @param artworkId - Artwork ID
 */
export const deleteArtworkImages = async (artworkId: string): Promise<void> => {
  console.warn('deleteArtworkImages: Bulk deletion not implemented - delete images individually');
};

/**
 * Get all images for a specific artwork
 * Note: This requires S3 list-objects API
 * @param artworkId - Artwork ID
 * @returns Array of image URLs
 */
export const getArtworkImages = async (artworkId: string): Promise<string[]> => {
  console.warn('getArtworkImages: Listing not implemented - images are stored in Firestore');
  return [];
};

/**
 * Upload multiple images at once
 * @param files - Array of files to upload
 * @param artworkId - Artwork ID
 * @returns Promise with array of successfully uploaded URLs
 */
export const uploadMultipleImages = async (files: File[], artworkId: string): Promise<string[]> => {
  try {
    const results = await Promise.allSettled(
      files.map(file => uploadImage(file, artworkId))
    );

    const successfulUploads: string[] = [];
    const failures: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulUploads.push(result.value);
      } else {
        failures.push(files[index].name);
        console.error(`Failed to upload ${files[index].name}:`, result.reason);
      }
    });

    if (failures.length > 0) {
      console.warn(`${failures.length} image(s) failed to upload:`, failures);
      if (successfulUploads.length === 0) {
        throw new Error(`All ${files.length} images failed to upload`);
      }
    }

    return successfulUploads;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
};

export default {
  uploadImage,
  deleteImage,
  deleteArtworkImages,
  getArtworkImages,
  uploadMultipleImages,
};
